import { createSvgEl } from '@learnforge/demo-render';

export interface DragHandleOptions {
  cx: number;
  cy: number;
  r?: number;
  snap?: (x: number, y: number) => { x: number; y: number };
  onMove(p: { x: number; y: number }): void;
  onEnd?(p: { x: number; y: number }): void;
}

export interface DragHandleHandle {
  element: SVGCircleElement;
  setPosition(x: number, y: number): void;
  setScale(s: number): void;
  destroy(): void;
}

export function createDragHandle(parent: SVGGElement, opts: DragHandleOptions): DragHandleHandle {
  const baseR = opts.r ?? 0.3;
  const el = createSvgEl('circle', {
    class: 'tg-handle',
    cx: opts.cx,
    cy: opts.cy,
    r: baseR,
    'data-no-pan': 'true',
  });
  parent.appendChild(el);

  let dragging = false;

  function toMath(ev: PointerEvent): { x: number; y: number } | null {
    const svg = el.ownerSVGElement;
    if (!svg) return null;
    const parentEl = el.parentNode as SVGGraphicsElement | null;
    if (!parentEl || typeof parentEl.getScreenCTM !== 'function') return null;
    const ctm = parentEl.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = ev.clientX;
    pt.y = ev.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  function applyPoint(p: { x: number; y: number }, end: boolean) {
    const snapped = opts.snap ? opts.snap(p.x, p.y) : p;
    el.setAttribute('cx', String(snapped.x));
    el.setAttribute('cy', String(snapped.y));
    opts.onMove(snapped);
    if (end && opts.onEnd) opts.onEnd(snapped);
  }

  const onDown = (ev: PointerEvent) => {
    dragging = true;
    el.setPointerCapture(ev.pointerId);
    ev.preventDefault();
  };
  const onMove = (ev: PointerEvent) => {
    if (!dragging) return;
    const p = toMath(ev);
    if (!p) return;
    applyPoint(p, false);
  };
  const onUp = (ev: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    el.releasePointerCapture(ev.pointerId);
    const p = toMath(ev);
    if (p) applyPoint(p, true);
  };

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onUp);

  return {
    element: el,
    setPosition(x, y) {
      el.setAttribute('cx', String(x));
      el.setAttribute('cy', String(y));
    },
    setScale(s) {
      el.setAttribute('r', String(baseR * s));
    },
    destroy() {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.remove();
    },
  };
}
